"use client";

import clsx from "clsx";
import React, { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Tooltip from "@/components/library/Tooltip";
import Icon, { ICON_SIZES } from "@/components/library/Icon";
import { getCellText, writeClipboardMulti } from "./utils";
import { TableHoverContext } from "./TableHoverContext";

export const MarkdownTableTh: React.FC<
  React.HTMLAttributes<HTMLTableCellElement> & { children?: React.ReactNode }
> = (props) => {
  const cellRef = useRef<HTMLTableCellElement>(null);
  const [copied, setCopied] = useState(false);
  const { t: ct } = useTranslation("common");
  const hoverCtx = useContext(TableHoverContext);

  const onCopyColumn = () => {
    const th = cellRef.current;
    if (!th) return;
    const table = th.closest("table") as HTMLTableElement | null;
    if (!table) return;
    const colIndex = th.cellIndex;
    const values: string[] = [];
    Array.from(table.rows).forEach((row) => {
      const cell = row.cells[colIndex] as HTMLTableCellElement | undefined;
      if (cell) values.push(getCellText(cell));
    });
    if (values.length === 0) return;
    const tsv = values.join("\n");
    const html = `<!DOCTYPE html><html><body><table>${values
      .map((v) => `<tr><td>${v}</td></tr>`)
      .join("")}</table></body></html>`;
    writeClipboardMulti(tsv, html).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };

  const { children, ...rest } = props;
  const colIndex = cellRef.current?.cellIndex ?? -1;
  const showColCopy =
    hoverCtx?.hoveredColIndex !== null &&
    hoverCtx?.hoveredColIndex === colIndex;

  return (
    <th
      ref={cellRef}
      onMouseEnter={() => {
        const idx = cellRef.current?.cellIndex ?? null;
        hoverCtx?.setHoveredColIndex(idx);
      }}
      {...rest}
    >
      <div className="relative">
        <div className="min-w-0">{children}</div>
        <div
          className={clsx(
            "absolute -top-1 -right-1 z-10 transition-opacity duration-150",
            showColCopy
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          )}
          data-copy-control
        >
          {copied ? (
            <span className="text-[10px] text-500 bg-panel/90 backdrop-blur-sm rounded px-1.5 py-0.5">{ct("copied")}</span>
          ) : (
            <Tooltip content={ct("copyOptions.copyColumn")} className="min-w-max">
              <button
                type="button"
                onClick={onCopyColumn}
                className="inline-flex items-center p-0.5 rounded text-300 hover:text-500 transition-colors"
                aria-label={ct("copyOptions.copyColumn")}
                data-copy-control
              >
                <Icon icon="copy" size={ICON_SIZES.XS} />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </th>
  );
};
